/**
 * Sms confirmation of a booking. The api requires this when it suspects the traffic is not a real
 * guest (failed bot check, abnormal booking volume, or an active fraud alert), and answers a
 * finalize attempt with 428 Precondition Required until the code is confirmed.
 */

/** Outcome of trying to send a code. Mirrors TwoFactorChallengeStatus in the api. */
export enum TwoFactorChallengeStatus {
    Sent = "Sent",
    MissingMobile = "MissingMobile",
    CooldownActive = "CooldownActive",
    SendLimitReached = "SendLimitReached",
    DeliveryFailed = "DeliveryFailed",
}

/** Outcome of checking a code the guest entered. Mirrors TwoFactorVerificationStatus in the api. */
export enum TwoFactorVerificationStatus {
    Verified = "Verified",
    NoChallenge = "NoChallenge",
    Expired = "Expired",
    IncorrectCode = "IncorrectCode",
    RecipientMismatch = "RecipientMismatch",
    TooManyAttempts = "TooManyAttempts",
}

/** Body of the 428 response. */
export interface TwoFactorChallenge {
    twoFactorRequired: boolean;
    challengeStatus?: TwoFactorChallengeStatus;
    verificationStatus?: TwoFactorVerificationStatus;
    codeExpiresAt?: string;
    retryAfterSeconds?: number;
    attemptsRemaining?: number;
}

/**
 * Thrown instead of surfacing the 428 as a booking failure: the booking is still alive and waiting
 * for a code, so the guest must not be sent back to start over.
 */
export class TwoFactorRequiredError extends Error {
    readonly challenge: TwoFactorChallenge;

    constructor(challenge: TwoFactorChallenge) {
        super("Two factor confirmation required");
        this.name = "TwoFactorRequiredError";
        this.challenge = challenge;
    }
}
